import { throttle } from 'lodash-es'
import createChunkBuffer, { ChunkFlushArgs } from './buffer'
import createChannel from './createChannel'
import {
  AIProcessorOptions,
  BaseStepEvent,
  ChannelMessage,
  CustomEvents,
  EventsHandler,
  EventsHandlerFactory,
  EventType,
  RawCustomEvent,
  ReportDataMessage,
  ReportStatusMessage,
  ToolCallEndEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from './types'

const DEBUG_ENABLED = typeof location !== 'undefined' && location.search.includes('ai_debug') // TODO

export default class AIProcessor<
  ExtraStepEvents extends object,
  ExtraCustomEvents extends object,
  ExtraClientCommand extends object,
> {
  private readonly options: AIProcessorOptions
  private channel: ReturnType<typeof createChannel>
  private eventsHandlersRegistry: {
    factory: EventsHandlerFactory<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
    instance?: EventsHandler<ExtraStepEvents, ExtraCustomEvents>
  }[] = []
  private chunkBuffer: ReturnType<typeof createChunkBuffer>
  private lastReportDataTimestamp = 0
  private isGenerating = false
  private ignoreOutputMessages = false
  private ignoreInterruptedMessages = false
  private destroyed = false
  private currentBackendConversationId?: string

  constructor(options: AIProcessorOptions) {
    this.options = { ...options }

    this.channel = createChannel({
      channelName: options.channelName,
      channelToken: options.channelToken,
      onMessage: this.handleMessage,
      onPerformFail: () => {
        this.forceInterrupt()
        this.options.onPerformFail?.()
      },
    })
    this.chunkBuffer = createChunkBuffer({}, this.handleChunkFlush)
  }

  private handleChunkFlush = (args: ChunkFlushArgs) => {
    let handled = false
    for (const eventsHandler of this.eventsHandlers) {
      if (eventsHandler.onChunkFlush?.(args)) handled = true
    }
    if (handled) return

    if (!args.done && !args.interrupted && !args.chunk) return
    const chunkType = args.key === 'thinking' ? 'thinking' : 'text'
    this.upsertMessage({
      chunks: [{ key: args.key, type: chunkType, content: args.chunk }],
      interrupted: args.interrupted,
      done: args.done,
    })
  }

  private handleMessage = (message: ChannelMessage) => {
    if (message.action === 'report_status' && message.input.status === 'current_metadata') {
      const input = message.input
      this.currentBackendConversationId = input.conversation_id
      this.options.onMessageStarted?.(this.currentBackendConversationId, input.message_id)
      this.upsertMessage({ backendMessageId: input.message_id })
      return
    }

    if (this.ignoreInterruptedMessages) return

    this.isGenerating = true
    switch (message.action) {
      case 'report_status':
        return this.handleReportStatus(message.input)
      case 'report_data':
        return this.handleReportData(message.input)
    }
  }

  private debugInfoBuffer: string[] = []

  private flushDebugInfo = throttle(
    () => {
      if (this.debugInfoBuffer.length) this.upsertMessage({ debugInfos: this.debugInfoBuffer })
      this.debugInfoBuffer = []
    },
    1000,
    { leading: true, trailing: true }
  )

  private reportDebugInfo(data: ReportDataMessage['input']['data']) {
    if (!DEBUG_ENABLED) return
    if (data.type === EventType.TextMessageContent || !(Object.values(EventType) as string[]).includes(data.type)) {
      return
    }

    this.debugInfoBuffer.push(
      `+${((Date.now() - this.lastReportDataTimestamp) / 1000).toFixed(3)}s `.padStart(10) +
        `event: ${data.type}\n` +
        `${JSON.stringify(data, null, 2)}\n`
    )
    this.flushDebugInfo()
  }

  private handleReportData({ data }: ReportDataMessage['input']) {
    this.reportDebugInfo(data)

    switch (data.type) {
      case EventType.RunError:
        return this.options.reportError?.({ step: data.code })
      case EventType.TextMessageContent:
        return this.chunkBuffer.reportChunk({ key: data.message_id, chunk: data.delta, index: data.raw_event.index })
      case EventType.ThinkingTextMessageContent:
        return this.chunkBuffer.reportChunk({ key: 'thinking', chunk: data.delta, index: data.raw_event.index })
      case EventType.StepStarted:
      case EventType.StepFinished:
        return this.handleUpsertStepUpdate(data)
      case EventType.ToolCallStart:
      case EventType.ToolCallResult:
      case EventType.ToolCallEnd:
        return this.handleUpsertStepUpdate(data)
      case EventType.Custom:
        return this.handleCustomEvent(data)
    }
  }

  private handleCustomEvent(rawData: RawCustomEvent) {
    let data: CustomEvents
    if (typeof rawData.value === 'string') {
      data = { ...rawData, value: JSON.parse(rawData.value) as CustomEvents['value'] }
    } else {
      data = rawData as unknown as CustomEvents
    }

    switch (data.name) {
      case 'content_sensitive':
        return this.handleReportCheckRiskRejected()
      default:
        for (const eventsHandler of this.eventsHandlers) {
          eventsHandler.onExtraCustomEvents?.(data as ExtraCustomEvents)
        }
    }
  }

  private toolCallMaps: Record<string, { toolName: string }> = {}

  private handleUpsertStepUpdate(data: BaseStepEvent | ToolCallStartEvent | ToolCallResultEvent | ToolCallEndEvent) {
    const toolName = 'tool_call_id' in data ? this.toolCallMaps[data.tool_call_id]?.toolName : undefined

    if (data.type === EventType.ToolCallStart) {
      this.toolCallMaps[data.tool_call_id] = { toolName: data.tool_call_name }
      this.upsertMessage({ stepUpdate: { step: data.tool_call_name, done: false } })
    } else if (data.type === EventType.ToolCallResult) {
      if (!toolName) return
      let parsedContent: unknown = data.content
      try {
        parsedContent = JSON.parse(data.content)
      } catch {
        // ignore
      }
      this.upsertMessage({ stepUpdate: { step: toolName, result: parsedContent } })
    } else if (data.type === EventType.ToolCallEnd) {
      if (toolName) this.upsertMessage({ stepUpdate: { step: toolName, done: true } })
    } else {
      this.upsertMessage({
        stepUpdate: { step: data.step_name, done: data.type === EventType.StepFinished },
      })
    }
    for (const eventsHandler of this.eventsHandlers) {
      eventsHandler.onExtraStepEvents?.({
        ...data,
        ...(toolName ? { tool_call_name: toolName } : undefined),
      } as ExtraStepEvents)
    }
  }

  private handleReportStatus(input: ReportStatusMessage['input']) {
    switch (input.status) {
      case 'interrupted':
        return this.chunkBuffer.reportChunk({ interrupted: true })
      case 'stopped':
        return this.handleStopped()
      case 'payment_required':
        return this.forceInterrupt()
    }
  }

  private async handleStopped(interrupted?: true) {
    this.chunkBuffer.reportChunk({ flush: true })

    let handled = false
    for (const eventsHandler of this.eventsHandlers) {
      if ((await eventsHandler.onStopped?.(interrupted)) && !interrupted) handled = true
    }
    if (handled) return

    this.chunkBuffer.reportChunk({ done: true, interrupted })
    this.upsertMessage({ done: true, interrupted })
    this.isGenerating = false
  }

  private handleReportCheckRiskRejected() {
    this.upsertMessage({ isCheckRiskRejectedMessage: true })
    this.forceInterrupt()
    for (const eventsHandler of this.eventsHandlers) {
      eventsHandler.onCheckRiskRejected?.()
    }
  }

  private get eventsHandlers() {
    for (const item of this.eventsHandlersRegistry) {
      if (!item.instance) item.instance = item.factory(this)
    }
    return this.eventsHandlersRegistry.map(item => item.instance!)
  }

  get backendConversationId() {
    return this.options.getBackendConversationId
      ? this.options.getBackendConversationId()
      : this.currentBackendConversationId
  }

  upsertMessage: NonNullable<typeof this.options.upsertMessage> = payload => {
    if (this.ignoreOutputMessages || this.destroyed) return

    this.options.upsertMessage?.(payload)
  }

  reportChunk: typeof this.chunkBuffer.reportChunk = args => {
    this.chunkBuffer.reportChunk(args)
  }

  reset() {
    for (const item of this.eventsHandlersRegistry) {
      item.instance = undefined
    }
    this.chunkBuffer.resetBuffer()
  }

  forceInterrupt() {
    this.ignoreInterruptedMessages = true
    if (this.isGenerating) {
      this.channel.perform('interrupt', { conversation_id: this.backendConversationId }, true)
      this.handleStopped(true)
    }
    this.chunkBuffer.resetBuffer()
  }

  prepare(ignoreOutputMessages = false) {
    this.isGenerating = true
    this.ignoreInterruptedMessages = false
    this.ignoreOutputMessages = ignoreOutputMessages
    this.chunkBuffer.resetBuffer()
    for (const eventsHandler of this.eventsHandlers) {
      eventsHandler.onPrepare?.()
    }
    this.lastReportDataTimestamp = Date.now()
  }

  perform<A extends keyof ExtraClientCommand>(action: A, payload: ExtraClientCommand[A]) {
    if (!this.isGenerating) this.prepare(true)

    this.channel.perform(action as string, payload)
  }

  registerEventsHandler(
    eventsHandlerFactory: EventsHandlerFactory<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
  ) {
    if (this.eventsHandlersRegistry.some(item => item.factory === eventsHandlerFactory)) return

    this.eventsHandlersRegistry.push({ factory: eventsHandlerFactory })
  }

  unregisterEventsHandler(
    eventsHandlerFactory: EventsHandlerFactory<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
  ) {
    const index = this.eventsHandlersRegistry.findIndex(item => item.factory === eventsHandlerFactory)
    if (index === -1) return

    this.eventsHandlersRegistry.splice(index, 1)
  }

  destroy() {
    this.destroyed = true
    this.eventsHandlersRegistry.splice(0, this.eventsHandlersRegistry.length)
    this.ignoreInterruptedMessages = true

    // wait for interrupt message to be sent
    setTimeout(() => {
      this.channel.destroy()
    })
  }
}
